import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Code, ExternalLink, Star, Award, CheckCircle, Clock, X } from 'lucide-react';

const STATUS_BADGE = {
  submitted: { class: 'bg-amber-100 text-amber-700', icon: Clock, label: 'New' },
  reviewed:  { class: 'bg-blue-100 text-blue-700',   icon: CheckCircle, label: 'Reviewed' },
  approved:  { class: 'bg-emerald-100 text-emerald-700', icon: Star, label: 'Approved' },
  needs_revision: { class: 'bg-red-100 text-red-700', icon: X, label: 'Needs revision' },
};

const STATUS_OPTIONS = [
  { value: 'reviewed', label: 'Reviewed' },
  { value: 'approved', label: 'Approved' },
  { value: 'needs_revision', label: 'Needs Revision' },
];

export const TeacherCodingReview = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [active, setActive] = useState(null);
  const [form, setForm] = useState({ status: 'approved', teacher_feedback: '', badges: '', is_showcase: false });
  const [saving, setSaving] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const url = filter ? `/teacher/coding-submissions?status=${filter}` : '/teacher/coding-submissions';
      const res = await api.get(url);
      setItems(res.data || []);
    } catch { toast.error('Failed to load submissions'); }
    setLoading(false);
  }, [filter]);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openReview = (p) => {
    setActive(p);
    setForm({
      status: p.status === 'submitted' ? 'approved' : p.status,
      teacher_feedback: p.teacher_feedback || '',
      badges: (p.badges_earned || []).join(', '),
      is_showcase: !!p.is_showcase,
    });
  };

  const onReview = async () => {
    if (!active) return;
    setSaving(true);
    try {
      await api.put(`/teacher/coding-submissions/${active.id}/review`, {
        status: form.status,
        teacher_feedback: form.teacher_feedback,
        badges_earned: form.badges.split(',').map(b => b.trim()).filter(Boolean),
        is_showcase: form.is_showcase,
      });
      toast.success('Review saved');
      setActive(null);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Review failed'); }
    setSaving(false);
  };

  return (
    <>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Code className="w-6 h-6 text-violet-600" /> Coding Projects
          </h1>
          <p className="text-slate-500 font-semibold text-sm">Review student project submissions and award badges</p>
        </div>
        <Select value={filter || 'all'} onValueChange={v => setFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-40" data-testid="filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="submitted">New (Pending)</SelectItem>
            <SelectItem value="reviewed">Reviewed</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="needs_revision">Needs Revision</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? <div className="text-center py-12 text-slate-500">Loading…</div> :
       items.length === 0 ? (
         <div className="nb-card text-center py-12">
           <Code className="w-12 h-12 text-slate-300 mx-auto mb-3" />
           <p className="text-slate-500 font-semibold">No projects to review</p>
         </div>
       ) : (
         <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
           {items.map(p => {
             const badge = STATUS_BADGE[p.status] || STATUS_BADGE.submitted;
             const Icon = badge.icon;
             return (
               <div key={p.id} className="nb-card p-4" data-testid={`review-card-${p.id}`}>
                 {p.preview_image && (
                   <img src={p.preview_image} alt={p.title} className="w-full h-32 object-cover rounded-lg mb-3 border-2 border-slate-900" />
                 )}
                 <div className="flex items-center justify-between mb-2">
                   <span className="text-[10px] font-bold uppercase text-violet-700 bg-violet-100 px-2 py-0.5 rounded-full">{p.project_type}</span>
                   <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${badge.class}`}><Icon className="w-3 h-3 inline mr-1" />{badge.label}</span>
                 </div>
                 <h3 className="text-lg font-bold text-slate-900 mb-1">{p.title}</h3>
                 <p className="text-xs text-slate-500 mb-1">by {p.student_name}</p>
                 <p className="text-xs text-slate-500 line-clamp-2 mb-2">{p.description}</p>
                 {p.url && (
                   <a href={p.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs font-bold text-blue-600 hover:underline mb-2">
                     <ExternalLink className="w-3 h-3" /> Open
                   </a>
                 )}
                 <Button size="sm" className="w-full bg-violet-600 hover:bg-violet-700 font-bold mt-2" onClick={() => openReview(p)} data-testid={`open-review-${p.id}`}>
                   {p.status === 'submitted' ? 'Review' : 'Update Review'}
                 </Button>
               </div>
             );
           })}
         </div>
       )}

      <Dialog open={!!active} onOpenChange={(o) => { if (!o) setActive(null); }}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-bold">Review — {active?.title}</DialogTitle>
            <DialogDescription>by {active?.student_name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            {active?.description && <p className="text-sm text-slate-600">{active.description}</p>}
            {active?.url && <a href={active.url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 font-bold hover:underline">{active.url}</a>}
            {(active?.files || []).length > 0 && (
              <div className="flex flex-wrap gap-1">
                {active.files.map((f, i) => (
                  <a key={i} href={`${process.env.REACT_APP_BACKEND_URL}${f.url}`} target="_blank" rel="noopener noreferrer"
                    className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-bold rounded">📎 {f.original_name}</a>
                ))}
              </div>
            )}
            <div>
              <Label className="font-bold">Status</Label>
              <Select value={form.status} onValueChange={v => setForm(p => ({ ...p, status: v }))}>
                <SelectTrigger className="mt-1" data-testid="review-status"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="font-bold">Feedback</Label>
              <Textarea value={form.teacher_feedback} onChange={e => setForm(p => ({ ...p, teacher_feedback: e.target.value }))}
                placeholder="Constructive feedback for the student" className="mt-1 min-h-[100px]" data-testid="review-feedback" />
            </div>
            <div>
              <Label className="font-bold">Badges (comma-separated)</Label>
              <Input value={form.badges} onChange={e => setForm(p => ({ ...p, badges: e.target.value }))}
                placeholder="scratch-pro, creative-coder" className="mt-1" data-testid="review-badges" />
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="showcase" checked={form.is_showcase} onChange={e => setForm(p => ({ ...p, is_showcase: e.target.checked }))}
                className="w-4 h-4" data-testid="review-showcase" />
              <Label htmlFor="showcase" className="font-bold cursor-pointer">Feature in public Showcase</Label>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setActive(null)}>Cancel</Button>
              <Button onClick={onReview} disabled={saving} className="bg-violet-600 hover:bg-violet-700 font-bold" data-testid="save-review-btn">
                {saving ? 'Saving…' : 'Save Review'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TeacherCodingReview;
